import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';

export const usePatientData = (patientId, caseId, apiType = 'physician', apiClasses, fetchCaseDetails) => {
  const { CasesApi, PhysicianAPI, PrescriptionsAPI } = apiClasses || {};

  const [patientDetails, setPatientDetails] = useState(null);
  const [patientDetailsLoading, setPatientDetailsLoading] = useState(false);

  const [patientAllergies, setPatientAllergies] = useState([]);
  const [patientAllergiesLoading, setPatientAllergiesLoading] = useState(false);
  const [allergySearchResults, setAllergySearchResults] = useState([]);
  const [allergySearchLoading, setAllergySearchLoading] = useState(false);

  const [error, setError] = useState(null);

  const fetchPatientAllergies = useCallback(async () => {
    if (!patientId) return;
    
    setPatientAllergiesLoading(true);
    setError(null);
    
    try {
      if (!PrescriptionsAPI) {
        throw new Error('PrescriptionsAPI not provided');
      }
      const response = await PrescriptionsAPI.getPatientAllergies(patientId);
      setPatientAllergies(response.data.allergies?.data || []);
    } catch (err) {
      setError(err.message);
      message.error('Unable to fetch patient allergies');
    } finally {
      setPatientAllergiesLoading(false);
    }
  }, [patientId, PrescriptionsAPI]);

  const searchAllergies = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setAllergySearchResults([]);
      return;
    }
    
    setAllergySearchLoading(true);
    
    try {
      if (!PrescriptionsAPI) {
        throw new Error('PrescriptionsAPI not provided');
      }
      const response = await PrescriptionsAPI.searchAllergies(query);
      setAllergySearchResults(response.data.allergies?.data || []);
    } catch (err) {
      setError(err.message);
      message.error('Error searching allergies');
    } finally {
      setAllergySearchLoading(false);
    }
  }, [PrescriptionsAPI]);

  const addAllergiesToPatient = useCallback(async (allergies) => {
    try {
      if (!PrescriptionsAPI) {
        throw new Error('PrescriptionsAPI not provided');
      }
      const response = await PrescriptionsAPI.addAllergiesToPatient(allergies);
      setPatientAllergies(response.data.allergies?.data || []);
      return response.data.errors || [];
    } catch (err) {
      setError(err.message);
      message.error('Error adding allergies');
      return [];
    }
  }, [PrescriptionsAPI]);

  const updateMedicalInformation = useCallback(async (data) => {
    try {
      if (!PrescriptionsAPI) {
        throw new Error('PrescriptionsAPI not provided');
      }
      const response = await PrescriptionsAPI.updatePatient(patientId, data);
      if (response.data.success) {
        message.success('Medical information updated successfully!');
        await fetchCaseDetails();
        return true;
      } else {
        throw new Error(response.data.payload);
      }
    } catch (err) {
      setError(err.message);
      message.error(err.message);
      return false;
    }
  }, [patientId, PrescriptionsAPI, fetchCaseDetails]);

  useEffect(() => {
    if (caseId && !patientDetailsLoading && !patientDetails) {
      fetchCaseDetails();
    }
    if (patientId && !patientAllergiesLoading && patientAllergies.length === 0) {
      fetchPatientAllergies();
    }
  }, [caseId, patientId]);

  return {
    patientDetails,
    patientDetailsLoading,
    fetchCaseDetails,
    patientAllergies,
    patientAllergiesLoading,
    allergySearchResults,
    allergySearchLoading,
    fetchPatientAllergies,
    searchAllergies,
    addAllergiesToPatient,
    updateMedicalInformation,
    error,
    setError
  };
};
